import React from "react";
import { components } from "react-select";
import { getActiveColorStyle } from "utils/colorUtils";
import { getSelectStyles } from "utils/selectStyles";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

/** Tema 2 arama filtreleri — Select / react-select kontrol border */
export const THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT = "#EBEAED";
export const THEME2_SEARCH_FILTER_BORDER_COLOR_DARK = "#3A4049";
/** Karanlık tema — multiselect menüde seçili öğe arka planı */
export const THEME2_MULTISELECT_SELECTED_BG_DARK = "#27293d";
/** @deprecated Use getTheme2SearchFilterBorderColor() for theme-aware color */
export const THEME2_SEARCH_FILTER_BORDER_COLOR = THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT;
export const THEME2_SEARCH_FILTER_FIELD_WIDTH = "280px";
/** Status çoklu seçim — diğer filtrelerden biraz daha geniş */
export const THEME2_SEARCH_FILTER_STATUS_FIELD_WIDTH = "320px";
/** Accounts — Status / Location çoklu seçim (dört filtreli satır) */
export const THEME2_SEARCH_FILTER_ACCOUNTS_WIDE_FIELD_WIDTH = "400px";

export const getTheme2SearchFilterBorderColor = () => {
  const isLightTheme =
    typeof document !== "undefined" &&
    document.body.classList.contains("white-content");
  return isLightTheme
    ? THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT
    : THEME2_SEARCH_FILTER_BORDER_COLOR_DARK;
};

export const withTheme2SearchFilterBorderStyles = (styles) => {
  if (!styles?.control) return styles;
  const baseControl = styles.control;
  return {
    ...styles,
    control: (provided, state) => {
      const merged = baseControl(provided, state);
      if (state.isDisabled) {
        return { ...merged, boxShadow: "none" };
      }
      const borderColor = getTheme2SearchFilterBorderColor();
      return {
        ...merged,
        borderColor,
        border: `1px solid ${borderColor}`,
        boxShadow: "none",
      };
    },
  };
};

export const getTheme2SearchFilterColProps = (isTheme2, { filterColClass, md }) =>
  isTheme2
    ? { xs: "12", sm: "6", lg: "auto", className: `mb-2 ${filterColClass}` }
    : { md, className: "mb-2" };

export const getTheme2SearchFilterSingleSelectStyles = (activeColorStyle, isTheme2) => {
  if (!isTheme2) return undefined;
  const isLightTheme =
    typeof document !== "undefined" &&
    document.body.classList.contains("white-content");
  return withTheme2SearchFilterBorderStyles(
    getSelectStyles(activeColorStyle, isLightTheme)
  );
};

export const getTheme2SearchFilterMultiSelectStyles = (activeColorStyle, isTheme2) => {
  const styles = getTheme2MultiSelectStyles({ activeColorStyle, isTheme2 });
  return isTheme2 ? withTheme2SearchFilterBorderStyles(styles) : styles;
};

const THEME2_FORM_DISABLED_BG_LIGHT = "rgba(29, 37, 59, 0.05)";
const THEME2_FORM_DISABLED_BORDER_LIGHT = "rgba(29, 37, 59, 0.2)";
const THEME2_FORM_DISABLED_TEXT_LIGHT = "rgba(29, 37, 59, 0.4)";
const THEME2_FORM_DISABLED_BG_DARK = "rgba(255, 255, 255, 0.05)";
const THEME2_FORM_DISABLED_BORDER_DARK = "rgba(255, 255, 255, 0.2)";
const THEME2_FORM_DISABLED_TEXT_DARK = "rgba(255, 255, 255, 0.4)";

const THEME2_FORM_CONTROL_ENABLED = ":not(:disabled):not([disabled]):not([readonly])";

/** Tema 2 text input — Search Inventory arama alanı border / focus (form-control) */
export const getTheme2FormControlCss = ({ scopeSelector, fieldsSelector }) => {
  const target = fieldsSelector
    ? `${scopeSelector} ${fieldsSelector}`
    : scopeSelector;
  return `
  body.white-content ${target} .form-control${THEME2_FORM_CONTROL_ENABLED},
  body.white-content ${target} .form-control${THEME2_FORM_CONTROL_ENABLED}:focus {
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT} !important;
  }
  body:not(.white-content) ${target} .form-control${THEME2_FORM_CONTROL_ENABLED},
  body:not(.white-content) ${target} .form-control${THEME2_FORM_CONTROL_ENABLED}:focus {
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK} !important;
  }
  ${target} .form-control${THEME2_FORM_CONTROL_ENABLED}:focus {
    box-shadow: none !important;
  }
  ${target} .form-control {
    border-radius: 0 !important;
  }
  body.white-content ${target} .form-control:disabled,
  body.white-content ${target} .form-control[disabled],
  body.white-content ${target} .form-control[readonly],
  body.white-content ${target} fieldset[disabled] .form-control {
    background-color: ${THEME2_FORM_DISABLED_BG_LIGHT} !important;
    border-color: ${THEME2_FORM_DISABLED_BORDER_LIGHT} !important;
    color: ${THEME2_FORM_DISABLED_TEXT_LIGHT} !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }
  body:not(.white-content) ${target} .form-control:disabled,
  body:not(.white-content) ${target} .form-control[disabled],
  body:not(.white-content) ${target} .form-control[readonly],
  body:not(.white-content) ${target} fieldset[disabled] .form-control {
    background-color: ${THEME2_FORM_DISABLED_BG_DARK} !important;
    border-color: ${THEME2_FORM_DISABLED_BORDER_DARK} !important;
    color: ${THEME2_FORM_DISABLED_TEXT_DARK} !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
    opacity: 1 !important;
  }
`;
};

/** Tema 2 Select — Search Inventory filtre dropdown border */
export const getTheme2ReactSelectControlCss = ({ scopeSelector, fieldsSelector }) => {
  const target = fieldsSelector
    ? `${scopeSelector} ${fieldsSelector}`
    : scopeSelector;
  const enabledControl = ".react-select__control:not(.react-select__control--is-disabled)";
  return `
  body.white-content ${target} .react-select ${enabledControl},
  body.white-content ${target} .react-select ${enabledControl}:hover,
  body.white-content ${target} .react-select .react-select__control--is-focused:not(.react-select__control--is-disabled) {
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_LIGHT} !important;
    box-shadow: none !important;
  }
  body:not(.white-content) ${target} .react-select ${enabledControl},
  body:not(.white-content) ${target} .react-select ${enabledControl}:hover,
  body:not(.white-content) ${target} .react-select .react-select__control--is-focused:not(.react-select__control--is-disabled) {
    border-color: ${THEME2_SEARCH_FILTER_BORDER_COLOR_DARK} !important;
    box-shadow: none !important;
  }
  body.white-content ${target} .react-select .react-select__control--is-disabled {
    background-color: ${THEME2_FORM_DISABLED_BG_LIGHT} !important;
    border-color: ${THEME2_FORM_DISABLED_BORDER_LIGHT} !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
    opacity: 0.85 !important;
  }
  body.white-content ${target} .react-select .react-select__control--is-disabled .react-select__single-value {
    color: ${THEME2_FORM_DISABLED_TEXT_LIGHT} !important;
  }
  body:not(.white-content) ${target} .react-select .react-select__control--is-disabled {
    background-color: ${THEME2_FORM_DISABLED_BG_DARK} !important;
    border-color: ${THEME2_FORM_DISABLED_BORDER_DARK} !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
    opacity: 0.85 !important;
  }
  body:not(.white-content) ${target} .react-select .react-select__control--is-disabled .react-select__single-value {
    color: ${THEME2_FORM_DISABLED_TEXT_DARK} !important;
  }
`;
};

/** Tema 2 form alanı yazı boyutu — Sidebar Appearance Font size (`:root` CSS değişkeni) */
export const getTheme2FormTypographyCss = ({ scopeSelector, fieldsSelector }) => {
  const target = fieldsSelector
    ? `${scopeSelector} ${fieldsSelector}`
    : scopeSelector;
  const fontSize = THEME2_SIDEBAR_NAV_FONT_SIZE_VAR;
  return `
  ${target} label,
  ${target} .form-control,
  ${target} .form-group p {
    font-size: ${fontSize} !important;
  }
  ${target} .card .card-header {
    font-size: calc(${fontSize} + 2px) !important;
  }
  ${target} .react-select__single-value,
  ${target} .react-select__placeholder,
  ${target} .react-select__input-container,
  ${target} .react-select__input {
    font-size: ${fontSize} !important;
  }
`;
};

/** Tema 2 form-control + react-select — tek scope altında text input ve dropdown */
export const getTheme2FormFieldsCss = (options) =>
  `${getTheme2FormControlCss(options)}${getTheme2ReactSelectControlCss(options)}${getTheme2FormTypographyCss(options)}`;

const THEME2_DARK_SELECT_TEXT = "rgba(255, 255, 255, 0.95)";
const THEME2_DARK_SELECT_TEXT_MUTED = "rgba(255, 255, 255, 0.4)";
const THEME2_DARK_SELECT_OPTION_SELECTED_BG = "rgba(255, 255, 255, 0.1)";
const THEME2_DARK_SELECT_OPTION_FOCUSED_BG = "rgba(255, 255, 255, 0.05)";

/** Tema 2 koyu tema — react-select iç/dropdown metinleri beyaz; seçili/odaklı hafif bold */
export const withTheme2FormPageDarkSelectStyles = (styles) => {
  const isLightTheme =
    typeof document !== "undefined" &&
    document.body.classList.contains("white-content");
  if (!styles || isLightTheme) return styles;

  const wrap = (key, fn) => {
    const base = styles[key];
    if (typeof base !== "function") return base;
    return (provided, state) => fn(base(provided, state), state);
  };

  return {
    ...styles,
    option: wrap("option", (merged, state) => ({
      ...merged,
      color: THEME2_DARK_SELECT_TEXT,
      fontWeight: state.isSelected || state.isFocused ? 500 : 400,
      backgroundColor: state.isSelected
        ? THEME2_DARK_SELECT_OPTION_SELECTED_BG
        : state.isFocused
          ? THEME2_DARK_SELECT_OPTION_FOCUSED_BG
          : merged.backgroundColor,
    })),
    singleValue: wrap("singleValue", (merged, state) => ({
      ...merged,
      color: state.isDisabled ? THEME2_DARK_SELECT_TEXT_MUTED : THEME2_DARK_SELECT_TEXT,
      fontWeight: 500,
    })),
    input: wrap("input", (merged) => ({
      ...merged,
      color: THEME2_DARK_SELECT_TEXT,
      fontWeight: 400,
    })),
    placeholder: wrap("placeholder", (merged) => ({
      ...merged,
      color: THEME2_DARK_SELECT_TEXT_MUTED,
      fontWeight: 400,
    })),
    noOptionsMessage: wrap("noOptionsMessage", (merged) => ({
      ...merged,
      color: THEME2_DARK_SELECT_TEXT,
      fontWeight: 400,
    })),
    loadingMessage: wrap("loadingMessage", (merged) => ({
      ...merged,
      color: THEME2_DARK_SELECT_TEXT,
      fontWeight: 400,
    })),
  };
};

/** Tema 2 form sayfası — tek select kaynağı (koyu temada iç metin override) */
export const getTheme2FormPageDarkSelectStyles = (activeColorStyle, isTheme2) => {
  const base = getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2);
  if (!isTheme2) return base;
  return withTheme2FormPageDarkSelectStyles(base);
};

/** Form sayfaları için scope/CSS/sınıflar: `hooks/useTheme2FormPage` kullanın. */

/** Tema 2 arama sayfası filtre satırı — eşit genişlik + text input border */
export const getTheme2SearchFilterFieldCss = ({
  pageScopeSelector,
  filtersSelector,
  rowClass,
  colClass,
}) => `
  ${pageScopeSelector} ${filtersSelector} {
    margin-bottom: 0;
  }
  ${pageScopeSelector} .${rowClass} {
    flex-wrap: wrap;
    column-gap: 12px;
    row-gap: 0;
  }
  ${pageScopeSelector} .${colClass} {
    flex: 0 0 auto;
    width: auto;
    max-width: 100%;
  }
  ${pageScopeSelector} .${colClass} .form-group {
    width: ${THEME2_SEARCH_FILTER_FIELD_WIDTH};
    max-width: 100%;
    margin-bottom: 0.5rem;
  }
  @media (max-width: 575.98px) {
    ${pageScopeSelector} .${colClass} .form-group {
      width: 100%;
    }
  }
  ${getTheme2FormFieldsCss({
    scopeSelector: pageScopeSelector,
    fieldsSelector: filtersSelector,
  })}
`;

/** Tema 2 — çoklu seçim filtre sütunları (status, location vb.) diğerlerinden biraz daha geniş */
export const getTheme2SearchFilterStatusFieldCss = ({
  pageScopeSelector,
  filterColClass,
  wideModifiers = ["status"],
  fieldWidth = THEME2_SEARCH_FILTER_STATUS_FIELD_WIDTH,
}) => {
  const wideSelectors = wideModifiers
    .map(
      (modifier) =>
        `${pageScopeSelector} .${filterColClass}.${filterColClass}--${modifier} .form-group`
    )
    .join(",\n  ");

  return `
  ${wideSelectors} {
    width: ${fieldWidth} !important;
    max-width: 100%;
  }
  @media (max-width: 575.98px) {
    ${wideSelectors} {
      width: 100% !important;
    }
  }
`;
};

/**
 * Tema 2 — arama sayfalarındaki Status (ve benzeri) çoklu seçim dropdown’u.
 * prefix örnekleri: "search-inventory-status", "search-deals-status"
 */
export const createTheme2MultiSelectOption = (prefix) => {
  const optionClass = `${prefix}-option`;
  const labelClass = `${prefix}-option__label`;
  const removeClass = `${prefix}-option__remove`;

  const Theme2MultiSelectOption = (props) => {
    const { data, isSelected, selectProps, label } = props;

    const handleRemoveClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isSelected) return;
      const currentValue = selectProps.value || [];
      const nextValue = currentValue.filter((item) => item.value !== data.value);
      selectProps.onChange(nextValue, {
        action: "remove-value",
        removedValue: data,
      });
    };

    return (
      <components.Option {...props}>
        <div className={optionClass}>
          <span className={labelClass}>{label}</span>
          {isSelected ? (
            <span
              role="button"
              tabIndex={0}
              className={removeClass}
              aria-label={`Remove ${label}`}
              onMouseDown={handleRemoveClick}
              onClick={handleRemoveClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleRemoveClick(event);
                }
              }}
            >
              <i className="tim-icons icon-simple-remove" aria-hidden="true" />
            </span>
          ) : null}
        </div>
      </components.Option>
    );
  };

  Theme2MultiSelectOption.displayName = `Theme2MultiSelectOption(${prefix})`;
  return Theme2MultiSelectOption;
};

export const getTheme2MultiSelectStyles = ({
  activeColor,
  activeColorStyle: activeColorStyleProp,
  isTheme2,
}) => {
  const activeColorStyle = activeColorStyleProp || getActiveColorStyle(activeColor || "blue");
  const isLightTheme =
    typeof document !== "undefined" && document.body.classList.contains("white-content");
  const base = getSelectStyles(activeColorStyle, isLightTheme);

  const multiValue = (provided) => ({
    ...provided,
    backgroundColor: isLightTheme ? "#ffffff" : THEME2_MULTISELECT_SELECTED_BG_DARK,
    border: isLightTheme
      ? "1px solid rgba(29, 37, 59, 0.25)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: 0,
    ...(isTheme2 ? { flex: "0 0 auto", margin: "2px" } : {}),
  });
  const multiValueLabel = (provided) => ({
    ...provided,
    color: isLightTheme ? "#1d253b" : "#ffffff",
    fontSize: "0.875rem",
    fontWeight: 500,
  });
  const multiValueRemove = (provided) => ({
    ...provided,
    color: isLightTheme ? "#1d253b" : "#ffffff",
    borderLeft: isLightTheme
      ? "1px solid rgba(29, 37, 59, 0.25)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: isLightTheme
        ? "rgba(29, 37, 59, 0.08)"
        : "rgba(255, 255, 255, 0.1)",
      color: isLightTheme ? "#1d253b" : "#ffffff",
    },
  });

  if (!isTheme2) {
    return { ...base, multiValue, multiValueLabel, multiValueRemove };
  }

  return {
    ...base,
    control: (provided, state) => ({
      ...base.control(provided, state),
      minHeight: "32px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      flexWrap: "nowrap",
      flexDirection: "row",
      alignItems: "center",
      overflowX: "auto",
      overflowY: "hidden",
      padding: "2px 4px",
    }),
    multiValue,
    multiValueLabel,
    multiValueRemove,
    input: (provided) => ({
      ...base.input(provided),
      flex: "1 0 auto",
      minWidth: "4px",
      margin: 0,
    }),
    option: (provided, state) => {
      const baseOption = base.option(provided, state);
      const textColor = isLightTheme ? "#1d253b" : "#ffffff";
      const selectedBg = isLightTheme ? "#ffffff" : THEME2_MULTISELECT_SELECTED_BG_DARK;
      const selectedFocusedBg = isLightTheme ? "#eeeeee" : THEME2_MULTISELECT_SELECTED_BG_DARK;
      let backgroundColor = "transparent";
      if (state.isSelected) {
        backgroundColor = state.isFocused ? selectedFocusedBg : selectedBg;
      } else if (state.isFocused) {
        backgroundColor = isLightTheme
          ? "rgba(29, 37, 59, 0.05)"
          : "rgba(255, 255, 255, 0.05)";
      }
      return {
        ...baseOption,
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        cursor: "pointer",
        color: textColor,
        fontWeight: state.isSelected ? 600 : 400,
        backgroundColor,
      };
    },
  };
};

export const getTheme2MultiSelectSelectConfig = (prefix, isTheme2) => {
  if (!isTheme2) {
    return {
      components: undefined,
      classNames: undefined,
      hideSelectedOptions: true,
      controlClassName: "react-select",
    };
  }

  return {
    components: { Option: createTheme2MultiSelectOption(prefix) },
    classNames: {
      menu: () => `${prefix}-menu`,
      menuPortal: () => `${prefix}-menu-portal`,
    },
    hideSelectedOptions: false,
    controlClassName: `react-select ${prefix}-multi`,
  };
};

/** Tema 2 multiselect — portal menü + kontrol chip stilleri (Cards inline style için) */
export const getTheme2MultiSelectCss = ({ prefix, pageScopeSelector = "" }) => {
  const menuClass = `${prefix}-menu`;
  const optionClass = `${prefix}-option`;
  const labelClass = `${prefix}-option__label`;
  const removeClass = `${prefix}-option__remove`;
  const multiClass = `${prefix}-multi`;
  const scope = pageScopeSelector ? `${pageScopeSelector} ` : "";

  return `
  ${scope}.${multiClass} .react-select__value-container {
    flex-wrap: nowrap !important;
    flex-direction: row !important;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
  }
  ${scope}.${multiClass} .react-select__multi-value {
    flex: 0 0 auto;
    display: inline-flex;
    margin: 2px;
  }
  .${menuClass}.react-select__menu {
    z-index: 10000;
  }
  .${menuClass} .react-select__option {
    display: flex !important;
    align-items: center !important;
    padding: 8px 12px !important;
  }
  .${menuClass} .react-select__option--is-selected {
    color: #1d253b !important;
    font-weight: 600 !important;
    background-color: #ffffff !important;
  }
  .${menuClass} .react-select__option--is-selected.react-select__option--is-focused {
    color: #1d253b !important;
    font-weight: 600 !important;
    background-color: #eeeeee !important;
  }
  .${menuClass} .react-select__option--is-selected .${labelClass} {
    color: inherit !important;
    font-weight: 600 !important;
  }
  body:not(.white-content) .${menuClass} .react-select__option--is-selected {
    color: rgba(255, 255, 255, 0.95) !important;
    background-color: ${THEME2_MULTISELECT_SELECTED_BG_DARK} !important;
  }
  body:not(.white-content) .${menuClass} .react-select__option--is-selected.react-select__option--is-focused {
    background-color: ${THEME2_MULTISELECT_SELECTED_BG_DARK} !important;
  }
  .${optionClass} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
    min-width: 0;
    box-sizing: border-box;
  }
  .${labelClass} {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 4px;
  }
  .${removeClass} {
    flex: 0 0 22px;
    width: 22px;
    height: 22px;
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none !important;
    box-shadow: none !important;
    background: rgba(29, 37, 59, 0.08) !important;
    color: #64748b !important;
    cursor: pointer;
    padding: 0;
    font-size: 10px;
    line-height: 1;
    box-sizing: border-box;
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  .${removeClass} .tim-icons {
    font-size: 10px;
    line-height: 1;
    pointer-events: none;
  }
  .${removeClass}:hover,
  .${removeClass}:focus {
    background: rgba(29, 37, 59, 0.14) !important;
    color: #1d253b !important;
    outline: none;
  }
  .${menuClass} .react-select__option--is-selected .${removeClass} {
    background: rgba(255, 255, 255, 0.92) !important;
    color: #1d253b !important;
  }
  .${menuClass} .react-select__option--is-selected .${removeClass}:hover,
  .${menuClass} .react-select__option--is-selected .${removeClass}:focus {
    background: #ffffff !important;
    color: #0f172a !important;
  }
  body:not(.white-content) .${removeClass} {
    background: rgba(255, 255, 255, 0.1) !important;
    color: rgba(255, 255, 255, 0.65) !important;
  }
  body:not(.white-content) .${removeClass}:hover,
  body:not(.white-content) .${removeClass}:focus {
    background: rgba(255, 255, 255, 0.18) !important;
    color: rgba(255, 255, 255, 0.95) !important;
  }
  body:not(.white-content) .${menuClass} .react-select__option--is-selected .${removeClass} {
    background: rgba(0, 0, 0, 0.2) !important;
    color: rgba(255, 255, 255, 0.9) !important;
  }
`;
};
